/**
 * Minimal client-side bank CSV parse for Fusion Build (E1) when StatementFlow
 * is not configured. Honest: labels outputs as "client CSV parse".
 * Expected headers (flexible): date/txn_date, amount, direction|dr|cr|debit|credit,
 * description|narration, utr|ref (optional), counterparty (optional).
 */
import type { SFTxn } from '../../services/statementFlow';
import { statementToElements, type BankImportResult } from '../../data/fusionBankAdapter';

function splitCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = '';
  let inQ = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQ && line[i + 1] === '"') { cur += '"'; i++; }
      else inQ = !inQ;
    } else if (ch === ',' && !inQ) {
      out.push(cur.trim());
      cur = '';
    } else {
      cur += ch;
    }
  }
  out.push(cur.trim());
  return out;
}

function normHeader(h: string): string {
  return h.trim().toLowerCase().replace(/[\s_-]+/g, '');
}

const DATE_KEYS = ['date', 'txndate', 'transactiondate', 'valuedate'];
const AMOUNT_KEYS = ['amount', 'txnamount', 'value', 'amt'];
const DIR_KEYS = ['direction', 'drcr', 'type', 'txntype'];
const DESC_KEYS = ['description', 'narration', 'particulars', 'remarks', 'rawdescription'];
const UTR_KEYS = ['utr', 'ref', 'refnumber', 'rrn', 'chequeno'];
const CP_KEYS = ['counterparty', 'beneficiary', 'payee', 'payer', 'party'];

function findCol(headers: string[], keys: string[]): number {
  for (let i = 0; i < headers.length; i++) {
    if (keys.includes(headers[i])) return i;
  }
  return -1;
}

function parseDirection(raw: string, amount: number): 'Dr' | 'Cr' | null {
  const s = raw.trim().toLowerCase();
  if (!s) return amount < 0 ? 'Dr' : null;
  if (/^(dr|debit|out|withdrawal|paid|debit\s*amount)$/.test(s)) return 'Dr';
  if (/^(cr|credit|in|deposit|received|credit\s*amount)$/.test(s)) return 'Cr';
  if (s.includes('debit') || s === 'd') return 'Dr';
  if (s.includes('credit') || s === 'c') return 'Cr';
  return null;
}

export interface ClientCsvParseResult {
  accountId: string;
  accountLabel: string;
  txns: SFTxn[];
  elements: BankImportResult;
  summary: string;
}

/** Parse a simple bank CSV into StatementFlow-shaped rows + fusion elements. */
export function parseBankCsvClient(text: string, fileName: string): ClientCsvParseResult {
  const lines = text.replace(/^\uFEFF/, '').split(/\r?\n/).filter((l) => l.trim());
  if (lines.length < 2) throw new Error('CSV needs a header row and at least one transaction');

  const headers = splitCsvLine(lines[0]).map(normHeader);
  const iDate = findCol(headers, DATE_KEYS);
  const iAmt = findCol(headers, AMOUNT_KEYS);
  const iDir = findCol(headers, DIR_KEYS);
  const iDesc = findCol(headers, DESC_KEYS);
  const iUtr = findCol(headers, UTR_KEYS);
  const iCp = findCol(headers, CP_KEYS);

  // Separate debit/credit amount columns (common Indian export shape)
  const iDebit = findCol(headers, ['debit', 'debitamount', 'withdrawal']);
  const iCredit = findCol(headers, ['credit', 'creditamount', 'deposit']);

  if (iAmt < 0 && iDebit < 0 && iCredit < 0) {
    throw new Error('CSV missing amount / debit / credit column');
  }

  const txns: SFTxn[] = [];
  for (let r = 1; r < lines.length; r++) {
    const cols = splitCsvLine(lines[r]);
    if (cols.every((c) => !c)) continue;

    let amount = 0;
    let direction: 'Dr' | 'Cr' | null = null;

    if (iDebit >= 0 || iCredit >= 0) {
      const dr = Math.abs(Number(String(cols[iDebit] || '').replace(/[,₹]/g, '')) || 0);
      const cr = Math.abs(Number(String(cols[iCredit] || '').replace(/[,₹]/g, '')) || 0);
      if (dr > 0) { amount = dr; direction = 'Dr'; }
      else if (cr > 0) { amount = cr; direction = 'Cr'; }
      else continue;
    } else {
      amount = Math.abs(Number(String(cols[iAmt] || '').replace(/[,₹]/g, '')) || 0);
      if (!amount) continue;
      direction = iDir >= 0
        ? parseDirection(cols[iDir] || '', Number(String(cols[iAmt] || '').replace(/[,₹]/g, '')) || 0)
        : 'Dr';
      if (!direction) direction = 'Dr';
    }

    const desc = iDesc >= 0 ? (cols[iDesc] || '') : '';
    const cp = iCp >= 0 ? (cols[iCp] || '').trim() : '';
    const utr = iUtr >= 0 ? (cols[iUtr] || '').trim() : '';
    const date = iDate >= 0 ? (cols[iDate] || '') : '';

    const entities: SFTxn['entities'] = {};
    if (cp) entities.counterparty = { value: cp, source: 'csv', confidence: 1 };
    if (utr) entities.utr = { value: utr, source: 'csv', confidence: 1 };

    txns.push({
      id: `csv-${r}`,
      account_id: 'client-csv',
      txn_date: date || new Date().toISOString().slice(0, 10),
      amount,
      direction,
      raw_description: [desc, utr ? `UTR:${utr}` : ''].filter(Boolean).join(' | '),
      entities: Object.keys(entities).length ? entities : undefined,
      tags: utr ? ['utr'] : undefined,
    });
  }

  if (!txns.length) throw new Error('No transaction rows parsed from CSV');

  const accountLabel = fileName.replace(/\.[^.]+$/, '') || 'Imported account';
  const accountId = `csv-${accountLabel.replace(/\W+/g, '').slice(0, 24) || 'acct'}`;
  const elements = statementToElements({
    accountId,
    accountLabel,
    txns,
  });

  return {
    accountId,
    accountLabel,
    txns,
    elements,
    summary: `Client CSV parse · ${txns.length} txn(s) · ${elements.counterpartyCount} counterparty(ies)`,
  };
}
